import { Group } from '@unistocks/components/group/group';
import { Sheet, SheetProps } from '@unistocks/components/sheet/sheet';
import { withFeedback } from '@unistocks/shared/utils/haptics/with-feedback';
import { ReactElement, ReactNode } from 'react';
import {
  Adapt,
  ListItem,
  ListItemProps,
  Popover,
  PopoverContentProps,
  PopoverProps,
  Separator,
  withStaticProperties
} from 'tamagui';

export type MenuItemProps = ListItemProps & {
  /**
   * Whether the menu should close when the item is pressed.
   * Set to false when the item should keep the menu open (e.g. when the press
   * opens a dialog or another sheet).
   * @default true
   */
  closeOnPress?: boolean;
};

export type MenuProps = Omit<PopoverProps, 'children'> & {
  /** Element rendered inside `Popover.Trigger`. */
  trigger: ReactNode;
  /** Rendered as children of `Menu`. Only `Menu.Item` elements are expected. */
  children: ReactNode;
  /** Header title used by the adapted `Sheet` on small screens. */
  headerTitle?: string;
  /** Header subtitle used by the adapted `Sheet` on small screens. */
  headerSubtitle?: string;
  /** Header icon used by the adapted `Sheet` on small screens. */
  headerIcon?: ReactNode;
  /** Separator rendered between items. @default <Separator /> */
  separator?: ReactElement;
  /** Props forwarded to the underlying `Popover.Content`. */
  contentContainerProps?: Partial<PopoverContentProps>;
  /**
   * Scroll mode for the adapted `Sheet` on small screens.
   * @default 'view'
   */
  scrollMode?: SheetProps['scrollMode'];
};

const MenuItem = ({ closeOnPress = true, children, onPress, ...props }: MenuItemProps) => {
  const item = (
    <Group.Item>
      <ListItem
        cursor={onPress ? 'pointer' : undefined}
        size={'$5'}
        gap={'$3'}
        borderRadius={'$4'}
        scaleIcon={1.5}
        onPressIn={withFeedback}
        onPress={onPress}
        {...props}
        $md={{
          size: '$4',
          ...props.$md
        }}
      >
        {children}
      </ListItem>
    </Group.Item>
  );

  return closeOnPress ? <Popover.Close asChild>{item}</Popover.Close> : item;
};

MenuItem.displayName = 'MenuItem';

const MenuImpl = ({
  trigger,
  children,
  headerTitle,
  headerSubtitle,
  headerIcon,
  separator,
  contentContainerProps,
  scrollMode = 'view',
  ...popoverProps
}: MenuProps) => {
  return (
    <Popover stayInFrame resize placement="bottom-end" size={'$5'} offset={18} {...popoverProps}>
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>

      <Popover.Content
        elevation={'$1'}
        borderWidth={1}
        p={0}
        borderColor="$borderColor"
        enterStyle={{ y: -20, o: 0 }}
        exitStyle={{ y: -20, o: 0 }}
        transition={'quickLessBouncy'}
        {...contentContainerProps}
      >
        <Popover.Arrow borderWidth={1} />
        <Group separator={separator ?? <Separator />}>{children}</Group>
      </Popover.Content>

      <Adapt when="max-md">
        <Sheet
          enableDynamicSizing
          headerTitle={headerTitle}
          headerSubtitle={headerSubtitle}
          headerIcon={headerIcon}
          scrollMode={scrollMode}
          contentContainerStyle={{ paddingHorizontal: 0 }}
        >
          <Adapt.Contents />
        </Sheet>
      </Adapt>
    </Popover>
  );
};

MenuImpl.displayName = 'Menu';

export const Menu = withStaticProperties(MenuImpl, {
  Item: MenuItem
});
